<template>
  <div
    v-if="open"
    id="nl-prefix-panel"
    ref="panel"
    class="nl-picker"
    @keydown.esc.prevent="$emit('close', 'escape')"
  >
    <!-- search -->
    <div class="nl-picker__search">
      <font-awesome-icon icon="search" class="nl-picker__search-icon" />
      <label class="sr-only" for="nl-prefix-search">
        {{ $t('pages.newLogin.prefixPicker.searchLabel') }}
      </label>
      <input
        id="nl-prefix-search"
        ref="search"
        v-model="search"
        v-focus
        type="search"
        class="nl-picker__input"
        :placeholder="$t('pages.newLogin.prefixPicker.searchPlaceholder')"
        autocomplete="off"
        @keydown.down.prevent="focusOption(0)"
      >
    </div>

    <!-- loading -->
    <div v-if="loading" class="nl-picker__status" role="status">
      <b-spinner small />
      <span>{{ $t('pages.newLogin.prefixPicker.loading') }}</span>
    </div>

    <!-- error -->
    <p v-else-if="error" class="nl-picker__status" role="status">
      {{ $t('pages.newLogin.prefixPicker.error') }}
    </p>

    <!-- empty -->
    <p v-else-if="!filteredCountries.length" class="nl-picker__status" role="status">
      {{ $t('pages.newLogin.prefixPicker.noResults') }}
    </p>

    <!-- results -->
    <ul v-else class="nl-picker__list">
      <li v-for="(country, index) in filteredCountries" :key="country.code">
        <button
          ref="options"
          type="button"
          class="nl-picker__option"
          :class="{ 'nl-picker__option--current': isCurrent(country) }"
          :tabindex="index === activeIndex ? 0 : -1"
          :aria-current="isCurrent(country) ? 'true' : null"
          @click="$emit('select', country)"
          @keydown.down.prevent="focusOption(index + 1)"
          @keydown.up.prevent="focusOption(index - 1)"
        >
          <b-img :src="getFlag(country.code)" alt="" width="28" height="20" class="nl-picker__flag" />
          <span class="nl-picker__prefix">+{{ country.prefix }}</span>
          <span class="nl-picker__name">{{ country.name }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { matchString } from '@/utils'

export default {
  name: 'PrefixPicker',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    currentCountryPrefix: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      search: '',
      loading: false,
      error: false,
      activeIndex: 0
    }
  },
  computed: {
    ...mapState({
      countries: 'countryPrefixes'
    }),
    filteredCountries () {
      const filtered = this.countries.filter(country => {
        return matchString(this.search, ['+' + country.prefix, country.code, country.name])
      })
      // keep the current selection at the top of the list
      const index = filtered.findIndex(country => this.isCurrent(country))
      if (index > -1) {
        const [current] = filtered.splice(index, 1)
        filtered.unshift(current)
      }
      return filtered
    }
  },
  watch: {
    open (isOpen) {
      if (isOpen) {
        this.search = ''
        this.activeIndex = 0
        this.fetchPrefixes()
        // Register on a macrotask, not $nextTick: a microtask checkpoint runs
        // mid-bubble (between event-listener calls), so a $nextTick listener would
        // catch the very click that opened the picker and close it instantly.
        // setTimeout defers until after the opening click has fully finished.
        setTimeout(() => {
          if (this.open) document.addEventListener('click', this.onDocumentClick)
        }, 0)
      } else {
        document.removeEventListener('click', this.onDocumentClick)
      }
    },
    search () {
      this.activeIndex = 0
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    ...mapActions(['getCountryPrefixes']),
    async fetchPrefixes () {
      if (this.countries.length) return
      this.loading = true
      this.error = false
      try {
        await this.getCountryPrefixes()
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    isCurrent (country) {
      return country.code === this.currentCountryPrefix.code
    },
    getFlag (countryCode) {
      return `/img/flags/${countryCode}@1x.png`
    },
    focusOption (index) {
      const options = this.$refs.options || []
      if (!options.length) return
      if (index < 0) {
        this.activeIndex = 0
        if (this.$refs.search) this.$refs.search.focus()
        return
      }
      this.activeIndex = Math.min(index, options.length - 1)
      this.$nextTick(() => {
        const el = this.$refs.options[this.activeIndex]
        if (el) el.focus()
      })
    },
    onDocumentClick (event) {
      const panel = this.$refs.panel
      if (panel && !panel.contains(event.target)) {
        this.$emit('close', 'outside')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.nl-picker {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  padding: 8px;
  border: 1px solid $nl-border;
  border-radius: 8px;
  background-color: $nl-surface;
  box-shadow: $nl-card-shadow;

  &__search {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__search-icon {
    position: absolute;
    left: 12px;
    color: $nl-text-muted;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    height: 40px;
    padding: 8px 12px 8px 34px;
    border: 1px solid $nl-border;
    border-radius: 8px;
    background-color: $nl-surface;
    font-family: 'Source Sans Pro';
    font-size: 1rem;
    color: $nl-text;
    outline: none;

    &:focus {
      border-color: $nl-border-strong;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0;
    padding: 16px;
    font-family: 'Source Sans Pro';
    font-size: 0.9375rem;
    color: $nl-text-muted;
  }

  &__list {
    max-height: 260px;
    margin: 8px 0 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-family: 'Source Sans Pro';
    font-size: 1rem;
    color: $nl-text;
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: $nl-surface-subtle;
      outline: none;
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 2px $nl-border-strong;
    }

    &--current {
      font-weight: 600;
    }
  }

  &__flag {
    flex: 0 0 auto;
    border-radius: 2px;
  }

  &__prefix {
    flex: 0 0 auto;
    min-width: 44px;
    color: $nl-text-muted;
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
